import AudioPlayer from 'react-h5-audio-player';
import 'react-h5-audio-player/lib/styles.css';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {
  Box,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { dateTimeFormatter, getFormattedRuPhone } from 'core/utils/commonUtils';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import type ReactH5AudioPlayer from 'react-h5-audio-player';
import type { MangoCallInfo } from 'models/mangoCall';

function ParticipantChip({
  name,
  phone,
}: {
  name: string | null | undefined;
  phone: string;
}): JSX.Element {
  return (
    <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
      <Typography variant='body2' fontWeight={600} noWrap lineHeight={1.2}>
        {name ?? phone}
      </Typography>
      {name != null && (
        <Typography
          variant='caption'
          color='text.secondary'
          display='block'
          noWrap
          lineHeight={1.2}>
          {phone}
        </Typography>
      )}
    </Box>
  );
}

export interface MangoCallsPlayerHandle {
  togglePlay: () => void;
}

interface MangoCallsPlayerProps {
  call: MangoCallInfo;
  showSkipControls: boolean;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
  onPlayStateChange?: (playing: boolean) => void;
}

const MangoCallsPlayer = forwardRef<MangoCallsPlayerHandle, MangoCallsPlayerProps>(
  ({ call, showSkipControls, onPrev, onNext, onClose, onPlayStateChange }, ref) => {
    const audioPlayerRef = useRef<ReactH5AudioPlayer>(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useImperativeHandle(ref, () => ({
      togglePlay: () => {
        const audio = audioPlayerRef.current?.audio.current;
        if (audio) {
          if (audio.paused) {
            void audio.play();
          } else {
            audio.pause();
          }
        }
      },
    }));

    const handleDownload = async (): Promise<void> => {
      try {
        const response = await fetch(call.recordUrl);
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `call_${dateTimeFormatter.format(call.dateOfCall)}.mp3`;
        a.click();
        URL.revokeObjectURL(url);
      } catch {
        window.open(call.recordUrl, '_blank', 'noopener,noreferrer');
      }
    };

    return (
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: { xs: 0, lg: 280 },
          right: 0,
          zIndex: (theme) => theme.zIndex.appBar,
          px: 2,
          pt: 1,
          pb: 0.5,
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {isMobile ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <ParticipantChip
                    name={call.fromUser?.fullName ?? call.fromClient?.fullName}
                    phone={getFormattedRuPhone(call.fromNumber)}
                  />
                  <KeyboardArrowRightIcon
                    sx={{ flexShrink: 0, fontSize: 16, color: 'text.disabled' }}
                  />
                  <ParticipantChip
                    name={call.toUser?.fullName ?? call.toClient?.fullName}
                    phone={getFormattedRuPhone(call.toNumber)}
                  />
                </Box>
                <Typography variant='caption' color='text.disabled'>
                  {dateTimeFormatter.format(call.dateOfCall)}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <ParticipantChip
                  name={call.fromUser?.fullName ?? call.fromClient?.fullName}
                  phone={getFormattedRuPhone(call.fromNumber)}
                />
                <KeyboardArrowRightIcon
                  sx={{ flexShrink: 0, fontSize: 18, color: 'text.disabled' }}
                />
                <ParticipantChip
                  name={call.toUser?.fullName ?? call.toClient?.fullName}
                  phone={getFormattedRuPhone(call.toNumber)}
                />
                <Typography
                  variant='caption'
                  color='text.disabled'
                  noWrap
                  sx={{ flexShrink: 0, ml: 0.5 }}>
                  {dateTimeFormatter.format(call.dateOfCall)}
                </Typography>
              </Box>
            )}
          </Box>

          <Tooltip title='Скачать запись'>
            <IconButton
              size='small'
              onClick={() => {
                void handleDownload();
              }}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title='Закрыть'>
            <IconButton size='small' onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <AudioPlayer
          ref={audioPlayerRef}
          src={call.recordUrl}
          autoPlay
          showJumpControls={false}
          showSkipControls={showSkipControls}
          onClickPrevious={onPrev}
          onClickNext={onNext}
          onPlay={() => onPlayStateChange?.(true)}
          onPause={() => onPlayStateChange?.(false)}
          customAdditionalControls={[]}
          layout={isMobile ? 'stacked' : 'horizontal'}
          style={{ boxShadow: 'none', background: 'transparent', padding: 0 }}
        />
      </Paper>
    );
  }
);

MangoCallsPlayer.displayName = 'MangoCallsPlayer';

export default MangoCallsPlayer;
